import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useReactiveVar } from '@apollo/client';
import { cx } from '@emotion/css';
import { useNavigate } from 'react-router-dom';

import { CvBanner } from './cv-banner';
import { DISTANCE_UNIT } from '../../../../../constants/ecp-locator';
import { useStyles } from './store-detail.styles';
import { IStoreDetailProps } from './store-detail.models';
import { useStoreByType } from '../../../../../hooks/use-store-by-type';
import { isVendingMachine, roundRating } from '../../../../../utils/ecp-locator';
import { Icon, IconSizes, IconTypes } from '../../../../common';
import { scrollToTop } from '../../../../../utils/scroll/scroll-to-top';
import { StoreServices } from '../../../../common/store-services';
import { OrderCreateButton } from '../../order/order-create-button';
import { C2CMode } from '../../../../common/configuration-settings/configuration-settings.models';
import { mapEcpStoreToOrderStore } from '../../../../../utils/ecp-locator/convert-store-types';
import { orderStore } from '../../../../../graphql/order/order.cache';
import { BackButton } from '../../../../common/back-button-new';
import { InternalLinks } from '../../../../../constants';
import { getRoundDistance } from '../../../../../utils/distance';
import {
  setAllowedStoreGroupCodes,
} from '../../../../../graphql/configuration/configuration.cache';
import { isExternalAppointment } from '../../../../common/configuration-settings/utils';
import { EcpServices } from '../ecp-locator.models';
import { AppointmentRequestButton } from '../../appointment-request-button';
import { NoFittings } from '../no-fittings';
import { EcpStoreType } from '../../../../../rest/ecp-locator';
import { Button } from '../../../../common/button-new';
import { ConsultationButton } from '../consultation-button';
import { formatPhoneNumber, formatStorePhoneNumbers } from '../../../../../utils';

import { pushEcpLocatorEvent } from '../../../../../utils/analytics/push-ecp-locator-event';
import { EcpLocatorEvent } from '../../../../../models/ya-metrica-events';

export const StoreDetail = ({
  storeData,
  onStoreClose,
  isFitted,
  c2CMode,
  ecpService,
  isNoFittingsShow,
  hasAppointment,
  refetchProfile,
}: IStoreDetailProps): JSX.Element => {
  const classes = useStyles();

  const {
    name,
    rating,
    type,
    distance,
    street,
    openinghours,
    phonenumber,
    speciality,
    detailedLocation,
    appointmentUrl,
    c2cActivated,
    surgery,
    website,
    code,
  } = storeData;

  const titleRef = useRef<HTMLButtonElement>(null);
  const isNormalStore = !isVendingMachine(type);
  const address =
    isVendingMachine(type) && detailedLocation?.length ? `${street}, ${detailedLocation}` : street;
  const formattedPhoneNumbers = formatStorePhoneNumbers(phonenumber);
  const isVendingMachineShow = isVendingMachine(type);
  const allowedGroupCodes = useReactiveVar(setAllowedStoreGroupCodes);
  const isCvServiceEnabled = ecpService === EcpServices.cv;

  useEffect(() => {
    titleRef.current?.focus();
    scrollToTop();

    return () => onStoreClose(storeData.code);
  }, []);

  const navigate = useNavigate();

  const handleClick = () => {
    pushEcpLocatorEvent(EcpLocatorEvent.OrderLenses, code);
    const mappedStoreData = mapEcpStoreToOrderStore(storeData);
    orderStore(mappedStoreData);
    navigate(InternalLinks.orderCreate);
  };

  const handleOrderAnalyticsClick = () => {
    pushEcpLocatorEvent(EcpLocatorEvent.OrderLenses, code);
  };

  const handleClickVending = () => {
    pushEcpLocatorEvent(EcpLocatorEvent.LensomatShowAssortment, code);
    // Allow dataLayer event to be processed before navigation
    if (appointmentUrl) {
      setTimeout(() => {
        window.open(appointmentUrl, '_blank', 'noopener,noreferrer');
      }, 0);
    }
  };

  const handlePhoneClick = (): void => {
    pushEcpLocatorEvent(EcpLocatorEvent.PhoneCall, code);
  };

  const handleBackButtonClick = () => {
    onStoreClose(storeData.code);
  };

  const isBuy = ecpService === EcpServices.buy && c2CMode !== C2CMode.disabled && c2cActivated;
  const url = isExternalAppointment(allowedGroupCodes, storeData.storeGroupCode, appointmentUrl)
    ? appointmentUrl ?? undefined
    : undefined;

  return (
    <div data-testid="store-detail" className={classes.root}>
      <BackButton onClick={handleBackButtonClick} />
      <h1 className={classes.storeName}>{name}</h1>
      <div className={cx(classes.content, { [classes.contentWithBorder]: isNormalStore })}>
        <div className={classes.contentPartial}>
          <p data-testid="store-detail-rating-bar" className={classes.ratingWrapper}>
            {rating && (
              <span
                data-testid="store-detail-rating"
                className={cx(classes.pointSeparator, classes.rating)}
              >
                {roundRating(rating)}
              </span>
            )}
            <span data-testid="store-detail-type" className={classes.pointSeparator}>
              {useStoreByType(type)}
            </span>
            {distance && (
              <span data-testid="store-detail-distance">
                {`${getRoundDistance(distance)} ${DISTANCE_UNIT}`}
              </span>
            )}
          </p>
          {isVendingMachineShow && (
            <p data-testid="points-only" className={classes.pointsOnly}>
              <Icon type={IconTypes.attention} aria-hidden="true" />
              <FormattedMessage id="ecp.pointsOnly.label" />
            </p>
          )}
          <p data-testid="store-detail-address" className={classes.street}>
            {street && <Icon type={IconTypes.mapMarkerBlack} size={IconSizes.xsm} aria-hidden="true" />}
            {address}
          </p>
          {isVendingMachineShow && (
            <p data-testid="pay-card" className={classes.payCard}>
              <Icon type={IconTypes.creditCard} aria-hidden="true" />
              <FormattedMessage id="ecp.payCard.label" />
            </p>
          )}
        </div>
        <div className={classes.contentPartial}>
          <p data-testid="store-detail-hours" className={classes.hours}>
            {openinghours && (
              <Icon type={IconTypes.clockBlack} size={IconSizes.xs} aria-hidden="true" />
            )}
            {openinghours}
          </p>
          {formattedPhoneNumbers.length > 0 && (
            <p className={classes.phoneWrapper}>
              <Icon type={IconTypes.phoneBlack} size={IconSizes.xs} aria-hidden="true" />
              {formattedPhoneNumbers.map((formattedPhoneNumber, index) => (
                <React.Fragment key={formattedPhoneNumber}>
                  {index > 0 && ', '}
                  <a
                    data-testid="store-detail-phone"
                    href={`tel:${formattedPhoneNumber}`}
                    className={classes.phone}
                    onClick={handlePhoneClick}
                  >
                    {formattedPhoneNumber}
                  </a>
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      </div>
      {(isNormalStore && !surgery && !isCvServiceEnabled) && <StoreServices type={type} speciality={speciality} />}
      {(isNormalStore && !surgery && isCvServiceEnabled) && (
        <StoreServices
          speciality={speciality}
          serviceOverride={<FormattedMessage id="ecp.services.controlVisit.short" />}
        />
      )}
      {(isNormalStore && surgery) && <StoreServices speciality="cataract" />}
      {(isNormalStore && isNoFittingsShow) && <div className={classes.noFittings}><NoFittings /></div>}
      {(isNormalStore && isCvServiceEnabled && !hasAppointment) && <CvBanner />}
      {isNormalStore && (
        <div data-testid="call-to-action-block" className={classes.callToAction}>
          {(ecpService === EcpServices.fit || ecpService === EcpServices.cv || isNoFittingsShow)  && (
            <AppointmentRequestButton
              appointmentUrl={url}
              isRetailer={type === EcpStoreType.retailer}
              storeCode={code}
              testId="store-detail-appointment-request-button" // Used by YM
              ecpService={ecpService}
              refetchProfile={refetchProfile}
            />
          )}
          {(isBuy && !isNoFittingsShow) && (
            <OrderCreateButton
              isFitted={isFitted}
              onClick={handleClick}
              onBeforeAuthCheck={handleOrderAnalyticsClick}
              style="dark"
              text="ecp.order.button"
            />
          )}
          {ecpService === EcpServices.cataract && <ConsultationButton phone={formatPhoneNumber(phonenumber)} website={website} code={code} />}
        </div>
      )}
      {isVendingMachineShow && appointmentUrl && (
        <div className={classes.callToAction}>
          <Button
            style="dark"
            onClick={handleClickVending}
            testId="vending-stock-link"
          >
            <FormattedMessage id="ecp.vending.watchStock" />
          </Button>
        </div>
      )}
    </div>
  );
};
